import { useState } from "react";
import { PDFDocument } from "pdf-lib";
import { downloadBlob } from "../Helper/pdfThumpnails";
import Text from "./Text";
import Button from "./Buttons/Button";

export default function PDFMetadataEditor() {
    const [fileName, setFileName] = useState("");
    const [pdfBytes, setPdfBytes] = useState(null);
    const [meta, setMeta] = useState({ title: "", author: "", subject: "", keywords: "" });
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setError("");
        setLoading(true);
        try {
            const buffer = await file.arrayBuffer();
            const bytes = new Uint8Array(buffer);
            const pdfDoc = await PDFDocument.load(bytes);

            setMeta({
                title: pdfDoc.getTitle() || "",
                author: pdfDoc.getAuthor() || "",
                subject: pdfDoc.getSubject() || "",
                keywords: (pdfDoc.getKeywords() || "").toString(),
            });
            setFileName(file.name);
            setPdfBytes(bytes);
        } catch (err) {
            console.error(err);
            setError("PDF konnte nicht gelesen werden.");
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (field) => (e) => {
        setMeta((prev) => ({ ...prev, [field]: e.target.value }));
    };

    const saveMetadata = async () => {
        if (!pdfBytes) return;
        setSaving(true);
        setError("");
        try {
            const pdfDoc = await PDFDocument.load(pdfBytes);
            pdfDoc.setTitle(meta.title);
            pdfDoc.setAuthor(meta.author);
            pdfDoc.setSubject(meta.subject);
            pdfDoc.setKeywords(
                meta.keywords.split(",").map((k) => k.trim()).filter(Boolean)
            );
            pdfDoc.setModificationDate(new Date());

            const outBytes = await pdfDoc.save();
            downloadBlob(
                outBytes,
                fileName.replace(/\.pdf$/i, "") + "-bearbeitet.pdf",
                "application/pdf"
            );
        } catch (err) {
            console.error(err);
            setError("Fehler beim Speichern der Metadaten.");
        } finally {
            setSaving(false);
        }
    };

    const reset = () => {
        setFileName("");
        setPdfBytes(null);
        setMeta({ title: "", author: "", subject: "", keywords: "" });
        setError("");
    };

    return (
        <div className="flex flex-col gap-4">
            <Text type="body">
                PDF auswählen, um Titel, Autor, Thema und Stichwörter zu bearbeiten.
            </Text>

            {!fileName && (
                <input type="file" accept="application/pdf" onChange={handleFileChange} />
            )}

            {loading && <Text type="body">Wird geladen...</Text>}

            {fileName && !loading && (
                <div className="flex items-center justify-between">
                    <Text type="body" className="text-gray-500">{fileName}</Text>
                    <Button buttonColor="bg-gray-200" onClickAction={reset}>
                        Andere Datei wählen
                    </Button>
                </div>
            )}

            {pdfBytes && !loading && (
                <div className="flex flex-col gap-3">
                    <label className="flex flex-col gap-1 text-sm">
                        Titel
                        <input
                            className="border rounded-md p-2"
                            value={meta.title}
                            onChange={handleChange("title")}
                        />
                    </label>

                    <label className="flex flex-col gap-1 text-sm">
                        Autor
                        <input
                            className="border rounded-md p-2"
                            value={meta.author}
                            onChange={handleChange("author")}
                        />
                    </label>

                    <label className="flex flex-col gap-1 text-sm">
                        Thema
                        <input
                            className="border rounded-md p-2"
                            value={meta.subject}
                            onChange={handleChange("subject")}
                        />
                    </label>

                    <label className="flex flex-col gap-1 text-sm">
                        Stichwörter (kommagetrennt)
                        <input
                            className="border rounded-md p-2"
                            value={meta.keywords}
                            onChange={handleChange("keywords")}
                            placeholder="z.B. rechnung, 2026, projekt-x"
                        />
                    </label>
                </div>
            )}

            {error && <p style={{ color: "red" }}>{error}</p>}

            {pdfBytes && (
                <Button
                    buttonColor={saving ? "bg-gray-400" : "bg-green-100"}
                    onClickAction={saveMetadata}
                    disabled={saving}
                >
                    {saving ? "Wird gespeichert..." : "Metadaten speichern & herunterladen"}
                </Button>
            )}
        </div>
    );
}